import React, { useState } from "react";
import { formatRocDate } from "../../../utils/rocDate";
import {
  X,
  Calendar as CalIcon,
  Clock,
  FileText,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Cake, 
} from "lucide-react";
// 請確認路徑是否正確
import {
  formatDate,
  formatTime,
  getDaysInMonth,
} from "../../../utils/timeHelpers"; 
import {
  getLeaveTypeColor,
  formatDurationDisplay,
} from "../../../utils/leaveHelpers"; 
import { ROLES } from "../../../config/constants"; 

export default function LeaveCalendar({
  leaves,
  role,
  user,
  users = [], 
  onDelete,
  onEdit,
}) {
  const [selectedLeaveDetail, setSelectedLeaveDetail] = useState(null);
  // 用來控制生日詳情視窗的狀態
  const [selectedBirthday, setSelectedBirthday] = useState(null); 
  
  const [currentDate, setCurrentDate] = useState(new Date());

  const daysInMonth = getDaysInMonth(
    currentDate.getFullYear(),
    currentDate.getMonth()
  );
  const todayStr = formatDate(new Date());
  const monthStart = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const startDay = monthStart.getDay();

  // 權限判斷
  const canDelete = (leave) => {
    if (role === ROLES.ADMIN) return true;
    return user.uid === leave.uid && leave.status !== "approved";
  };

  const changeMonth = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentDate(newDate);
  };

  return (
    <div className="leave-calendar calendar-card bg-white rounded-xl shadow-sm border border-slate-100 p-4 relative">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => changeMonth(-1)}
          aria-label="上個月" title="上個月"
          className="lake-icon-button p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <h3 className="font-bold text-slate-700 text-lg flex items-center gap-2">
          <CalIcon size={18} className="text-blue-500" />
          {currentDate.getFullYear()}年 {currentDate.getMonth() + 1}月
        </h3>
        <button
          onClick={() => changeMonth(1)}
          aria-label="下個月" title="下個月"
          className="lake-icon-button p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="calendar-weekdays grid grid-cols-7 gap-1 mb-2 text-center text-xs font-bold text-slate-500">
        <div>日</div>
        <div>一</div>
        <div>二</div>
        <div>三</div>
        <div>四</div>
        <div>五</div>
        <div>六</div>
      </div>
      <div className="leave-calendar-grid grid grid-cols-7 gap-1 auto-rows-fr">
        {/* 填補月初空白天數 */}
        {Array.from({ length: startDay }).map((_, i) => (
          <div key={`empty-${i}`} className="calendar-empty bg-slate-50 h-24 rounded"></div>
        ))}
        
        {/* 渲染該月每一天 */}
        {daysInMonth.map((date) => {
          const dayStr = formatDate(date);
          const isToday = dayStr === todayStr;

          // 1. 篩選當日請假
          const dayLeaves = leaves.filter((l) => {
            const s = new Date(l.startDate);
            s.setHours(0, 0, 0, 0);
            const e = new Date(l.endDate);
            e.setHours(23, 59, 59, 999);
            return date >= s && date <= e;
          });

          // 2. 篩選當日壽星
          const dayBirthdays = users.filter((u) => {
            if (!u.birthday) return false;
            const [, month, day] = u.birthday.split("-"); 
            return (
                parseInt(month) === date.getMonth() + 1 && 
                parseInt(day) === date.getDate()
            );
          });

          return (
            <div
              key={dayStr}
              className={`calendar-day leave-day border h-24 rounded p-1 relative overflow-hidden transition-colors ${
                isToday
                  ? "leave-today-cell border-amber-400 ring-2 ring-amber-400"
                  : "border-slate-100 bg-white hover:bg-slate-50"
              }`}
            >
              {isToday ? (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
                  {date.getDate()}
                </span>
              ) : (
                <span
                  className={`text-xs font-bold ${
                    date.getDay() === 0 || date.getDay() === 6
                      ? "text-red-400"
                      : "text-slate-700"
                  }`}
                >
                  {date.getDate()}
                </span>
              )}

              <div className="flex flex-col gap-1 mt-1 overflow-y-auto max-h-[70px] scrollbar-hide">
                
                {/* 顯示壽星 (修改：無底色 + 蛋糕圖案) */}
                {dayBirthdays.map((u) => (
                  <div 
                    key={`bday-${u.uid}`}
                    // 移除背景色，改用透明背景，Hover時有淡淡的灰色
                    className="birthday-chip mb-1 w-full flex items-center gap-1.5 px-1 py-1 rounded cursor-pointer hover:bg-slate-50 transition-colors group"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBirthday(u); // 點擊開啟彈窗
                    }}
                  >
                    {/* 蛋糕圖案：紫色 */}
                    <Cake size={16} className="text-purple-500 shrink-0" />
                    
                    {/* 姓名：深色文字 */}
                    <span className="text-xs font-bold text-slate-600 truncate group-hover:text-purple-600 transition-colors">
                        {u.name || "壽星"}
                    </span>
                  </div>
                ))}

                {/* 顯示請假條 */}
                {dayLeaves.map((l) => (
                  <div
                    key={l.id}
                    className={`leave-chip ${getLeaveTypeColor(
                      l.type
                    )} text-white text-[10px] px-1 rounded truncate shadow-sm cursor-pointer hover:opacity-80 transition-opacity ${
                      l.status !== "approved"
                        ? "opacity-75 ring-1 ring-offset-1 ring-slate-300"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLeaveDetail(l);
                    }}
                  >
                    {l.userName} {l.status !== "approved" && "*"}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* 生日詳情 Modal (保持功能，讓手機版點擊能看到大圖與祝福) */}
      {selectedBirthday && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
          onClick={() => setSelectedBirthday(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform scale-100 transition-transform relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 背景裝飾 */}
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-purple-500 to-pink-500"></div>
            
            <button
                onClick={() => setSelectedBirthday(null)}
                className="absolute top-3 right-3 bg-white/20 text-white p-1.5 rounded-full hover:bg-white/30 transition-colors z-10"
            >
                <X size={20} />
            </button>

            <div className="pt-12 px-6 pb-6 text-center relative z-0">
                {/* 大蛋糕圖示 */}
                <div className="w-20 h-20 bg-white rounded-full mx-auto shadow-lg flex items-center justify-center border-4 border-purple-100 mb-4">
                    <Cake size={40} className="text-purple-500" />
                </div>
                
                <h3 className="text-2xl font-bold text-slate-800 mb-1">
                    Happy Birthday!
                </h3>
                <p className="text-purple-600 font-bold text-lg mb-4">
                    {selectedBirthday.name}
                </p>
                
                <div className="bg-purple-50 rounded-xl p-4 text-sm text-slate-600 space-y-2">
                    <div className="flex justify-center items-center gap-2">
                        <CalIcon size={16} className="text-purple-400"/>
                        <span>生日日期（民國）：{formatRocDate(selectedBirthday.birthday)}</span>
                    </div>
                    <p className="italic opacity-80">
                        祝您生日快樂，工作順心！
                    </p>
                </div>

                <button 
                    onClick={() => setSelectedBirthday(null)}
                    className="mt-6 w-full bg-purple-600 text-white py-2.5 rounded-xl font-bold shadow hover:bg-purple-700 transition-all"
                >
                    關閉
                </button>
            </div>
          </div>
        </div>
      )}

      {/* 請假詳情 Modal */}
      {selectedLeaveDetail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
          onClick={() => setSelectedLeaveDetail(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform scale-100 transition-transform"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`p-5 text-white flex justify-between items-start ${getLeaveTypeColor(
                selectedLeaveDetail.type
              )}`}
            >
              <div>
                <h3 className="text-xl font-bold">
                  {selectedLeaveDetail.userName}
                </h3>
                <span className="text-white/80 text-sm font-medium bg-black/10 px-2 py-0.5 rounded mt-1 inline-block">
                  {selectedLeaveDetail.typeName}{" "}
                  {selectedLeaveDetail.status !== "approved" && "(簽核中)"}
                </span>
              </div>
              <button
                onClick={() => setSelectedLeaveDetail(null)}
                className="bg-white/20 p-1.5 rounded-full hover:bg-white/30 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                <div className="text-sm">
                  <div className="font-bold text-slate-700">
                    開始: {formatDate(new Date(selectedLeaveDetail.startDate))}{" "}
                    {formatTime(new Date(selectedLeaveDetail.startDate))}
                  </div>
                  <div className="font-bold text-slate-700">
                    結束: {formatDate(new Date(selectedLeaveDetail.endDate))}{" "}
                    {formatTime(new Date(selectedLeaveDetail.endDate))}
                  </div>
                  <div className="text-slate-500 text-xs mt-1">
                    共{" "}
                    {formatDurationDisplay(
                      selectedLeaveDetail.days,
                      selectedLeaveDetail.hours
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-slate-400 mt-0.5" />
                <div className="text-sm">
                  <div className="text-xs text-slate-400 mb-1">請假事由</div>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-lg leading-relaxed">
                    {selectedLeaveDetail.reason}
                  </p>
                </div>
              </div>
              {canDelete(selectedLeaveDetail) && (
                <div className="pt-4 border-t flex justify-end">
                  <button
                    onClick={() => {
                      setSelectedLeaveDetail(null);
                      onDelete(selectedLeaveDetail.id);
                    }}
                    className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg font-bold hover:bg-red-100 transition-colors text-sm"
                  >
                    <Trash2 size={16} /> 刪除此假單
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
